import { AnimatePresence, motion } from 'framer-motion';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  Control,
  UseFormClearErrors,
  UseFormSetError,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { HelpIconVariant } from '../../../../../assets/icons/help-icon';
import {
  CalendarIcon,
  ChevronDownIcon,
  EventGrant,
  HelpIcon,
} from '../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import Button from '../../../../../components/button/index';
import { OverlayDatePicker as DatePicker } from '../../../../../components/date-picker/index';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../../../../../components/hover-card/index';
import { FormInput } from '../../../../../components/input/index';
import {
  Stakeholder as StakeholderHook,
  useFormat,
  useRequestSequence,
  VestingTaskHooks,
} from '../../../../../hooks/index';
import { ReverseVestingTerminationPreviewRow } from '../../../../../hooks/stakeholder/index';
import { StringKey } from '../../../../../lang/index';
import { Stakeholder, stakeholderExTypeTitle } from '../../../../../types/stakeholder.types';
import { VestingTask } from '../../../../../types/vesting-tasks.types';
import { calcDayDifference } from '../../../../../utils/calc-day-difference.util';
import { coerceDateMs } from '../../../../../utils/coerce-date-ms.util';
import { toBackendDateFormat } from '../../../../../utils/to-backend-date-format.util';
import { toUpperCaseWords } from '../../../../../utils/to-uppercase-util.util';
import { shouldApplyDerivedLastVestingDate } from '../last-vesting-date.util';
import { FormSchema } from '../terminate-stakeholder-modal.validation';
import { ReverseVestingTable } from './reverse-vesting-table';
import { TerminationTypeCombobox } from './termination-type-combobox';

export type TableItemProps = {
  clearErrors: UseFormClearErrors<FormSchema>;
  companyId: string;
  control: Control<FormSchema>;
  editMode?: boolean;
  selectedId: string;
  setError: UseFormSetError<FormSchema>;
  setFormData: UseFormSetValue<FormSchema>;
  setSelectedId: React.Dispatch<React.SetStateAction<string>>;
  stakeholder: Stakeholder;
  stakeholderIndex: number;
};

export const TableItem: FC<TableItemProps> = ({
  control,
  setFormData,
  companyId,
  stakeholder,
  stakeholderIndex,
  selectedId,
  setSelectedId,
  editMode,
  clearErrors,
  setError,
}) => {
  const [isManually, setIsManually] = useState<boolean>(editMode ? true : false);
  const [isFinalDateManually, setIsFInalDateManually] = useState<boolean>(editMode ? true : false);
  const { format } = useFormat();
  const { terminateForm } = useWatch<FormSchema>({ control });
  const { vestingTasks } = VestingTaskHooks.useVestingTasks({
    companyId,
    stakeholderId: stakeholder.id,
    exercisable: null,
  });

  const isSelected = selectedId === stakeholder.id;

  const { calcTerminationLastVestingDate } = StakeholderHook.useTerminationLastVestingDate();
  const { predictTerminationVesting, isPending: isPredictionPending } =
    StakeholderHook.usePredictTerminationVesting();
  const { previewReverseVestingTermination, isPending: isReverseVestingPending } =
    StakeholderHook.usePreviewReverseVestingTermination();

  const [reverseVestingRows, setReverseVestingRows] = useState<
    ReverseVestingTerminationPreviewRow[]
  >([]);

  const [predictionByTaskId, setPredictionByTaskId] = useState<
    Record<string, { predictedReturnedToPool: number; predictedVestedShares: null | number }>
  >({});

  const vestingTaskIdKey = useMemo(() => vestingTasks.map((t) => t.id).join(','), [vestingTasks]);

  const terminationDateMs = coerceDateMs(
    terminateForm?.stakeholderItems?.[stakeholderIndex]?.terminationDate,
  );

  const lastVestingDateMs = coerceDateMs(
    terminateForm?.stakeholderItems?.[stakeholderIndex]?.lastVestingDate,
  );

  const beginLastVestingDateRequest = useRequestSequence();
  const beginPredictionRequest = useRequestSequence();
  const beginReverseVestingRequest = useRequestSequence();

  // Read by the projection callbacks instead of their effect's closure: a response that arrives
  // after the operator has picked a Last Vesting Date has to see that pick, not the state the
  // request was issued with.
  const lastVestingStateRef = useRef({ isManually, lastVestingDateMs });
  useEffect(() => {
    lastVestingStateRef.current = { isManually, lastVestingDateMs };
  }, [isManually, lastVestingDateMs]);

  const handleTerminationTypeChange = () => {
    setIsFInalDateManually(false);
  };

  // Mirrored into the form so the terminate payload carries it: only a picked Last Vesting Date
  // governs the reverse-vesting repurchase — a derived one is a grant trigger (1248h5336u5).
  useEffect(() => {
    setFormData(
      `terminateForm.stakeholderItems.${stakeholderIndex}.isLastVestingDateManual`,
      isManually,
    );
  }, [isManually, setFormData, stakeholderIndex]);

  useEffect(() => {
    setFormData(`terminateForm.stakeholderItems.${stakeholderIndex}.id`, stakeholder.id);
    if (vestingTasks.length > 0) {
      setFormData(
        `terminateForm.stakeholderItems.${stakeholderIndex}.grantItems`,
        vestingTasks.map((task) => ({
          ...task,
          fullVestingCalculatedDate: new Date(task.fullVestingCalculatedDate),
        })),
      );
    }
  }, [setFormData, stakeholder.id, stakeholderIndex, vestingTasks]);

  useEffect(() => {
    const terminationDateRaw = terminateForm?.stakeholderItems?.[stakeholderIndex]?.terminationDate;
    const lastVestingDateRaw = terminateForm?.stakeholderItems?.[stakeholderIndex]?.lastVestingDate;

    // Claimed before the early return as well, so clearing the dates cancels a pending write
    // rather than letting it land on an emptied table.
    const isCurrentRequest = beginPredictionRequest();

    const tMs = coerceDateMs(terminationDateRaw);
    if (terminationDateRaw == null || tMs == null || vestingTasks.length === 0) {
      setPredictionByTaskId({});
      return;
    }

    predictTerminationVesting(
      {
        companyId,
        data: {
          terminationDate: toBackendDateFormat(terminationDateRaw),
          vestingTaskIds: vestingTasks.map((t) => t.id),
          ...(lastVestingDateRaw
            ? { lastVestingDate: toBackendDateFormat(lastVestingDateRaw) }
            : {}),
        },
      },
      {
        onSuccess: (res) => {
          if (!isCurrentRequest()) return;

          const next: Record<
            string,
            { predictedReturnedToPool: number; predictedVestedShares: null | number }
          > = {};
          for (const t of res.tasks) {
            next[t.vestingTaskId] = {
              predictedVestedShares: t.predictedVestedShares,
              predictedReturnedToPool: t.predictedReturnedToPool,
            };
          }
          setPredictionByTaskId(next);
          vestingTasks.forEach((vt, idx) => {
            const p = res.tasks.find((x) => x.vestingTaskId === vt.id);
            if (p?.predictedVestedShares != null) {
              setFormData(
                `terminateForm.stakeholderItems.${stakeholderIndex}.grantItems.${idx}.vestedSharesCount`,
                p.predictedVestedShares,
              );
            }
          });
        },
      },
    );
    // eslint-disable-next-line -- intentional hook deps
  }, [
    beginPredictionRequest,
    companyId,
    predictTerminationVesting,
    setFormData,
    stakeholderIndex,
    terminationDateMs,
    lastVestingDateMs,
    vestingTaskIdKey,
  ]);

  // Reverse vesting is projected off the Last Vesting Date only once the operator picked it; a date
  // derived from the grants is a grant trigger, so the backend evaluates at the termination date
  // instead — the same rule the settlement applies. It falls back to today, so the section is
  // present from the moment the row is expanded rather than only once a date is picked.
  useEffect(() => {
    const terminationDateRaw = terminateForm?.stakeholderItems?.[stakeholderIndex]?.terminationDate;
    const lastVestingDateRaw = terminateForm?.stakeholderItems?.[stakeholderIndex]?.lastVestingDate;

    const isCurrentRequest = beginReverseVestingRequest();

    previewReverseVestingTermination(
      {
        companyId,
        stakeholderId: stakeholder.id,
        data: {
          terminationDate: toBackendDateFormat(terminationDateRaw ?? new Date()),
          ...(lastVestingDateRaw
            ? {
                isLastVestingDateManual: isManually,
                lastVestingDate: toBackendDateFormat(lastVestingDateRaw),
              }
            : {}),
        },
      },
      {
        onSuccess: ({ rows }) => {
          if (!isCurrentRequest()) return;
          setReverseVestingRows(rows);
        },
      },
    );
    // eslint-disable-next-line -- intentional hook deps (primitive ms fields, not stakeholderItems ref)
  }, [
    beginReverseVestingRequest,
    companyId,
    isManually,
    lastVestingDateMs,
    previewReverseVestingTermination,
    stakeholder.id,
    stakeholderIndex,
    terminationDateMs,
  ]);

  useEffect(() => {
    const terminationDateRaw = terminateForm?.stakeholderItems?.[stakeholderIndex]?.terminationDate;

    const isCurrentRequest = beginLastVestingDateRequest();

    if (vestingTasks.length > 0 && terminationDateRaw != null && !isManually) {
      const isSomeTasksHaveVesting = vestingTasks.some((task) => task.grantItem.plan.vesting);
      if (isSomeTasksHaveVesting) {
        const data = {
          terminationDate: toBackendDateFormat(terminationDateRaw),
          vestingTaskIds: vestingTasks.map(({ id }) => id),
        };
        calcTerminationLastVestingDate(
          {
            companyId,
            data,
          },
          {
            onSuccess: (data) => {
              // Both guards are live, not captured: `isManually` can have flipped while this was
              // in flight (the operator picking a date does not re-run this effect, so the
              // sequence alone would still call the response current), and the form value it is
              // compared against must be the one on screen.
              const { isManually: isManualNow, lastVestingDateMs: currentMs } =
                lastVestingStateRef.current;

              if (
                !shouldApplyDerivedLastVestingDate({
                  currentMs,
                  derivedMs: coerceDateMs(data),
                  isManual: isManualNow,
                  isStale: !isCurrentRequest(),
                })
              ) {
                return;
              }

              setFormData(
                `terminateForm.stakeholderItems.${stakeholderIndex}.lastVestingDate`,
                data,
              );
            },
          },
        );
      }
    }
    // eslint-disable-next-line -- intentional hook deps (avoid terminateForm churn)
  }, [
    beginLastVestingDateRequest,
    stakeholderIndex,
    vestingTaskIdKey,
    terminationDateMs,
    isManually,
    calcTerminationLastVestingDate,
    companyId,
    setFormData,
  ]);

  const hasGrants = vestingTasks.length > 0;
  const hasReverseVesting = reverseVestingRows.length > 0;

  // A founder who only holds reverse-vested shares gets no Grants section at all rather than an
  // empty one. The empty state is still worth rendering when the stakeholder has neither, so the
  // expanded row explains itself instead of opening onto nothing. Held back while the projection
  // is in flight so the empty state can't flash before the reverse-vesting rows arrive.
  const isGrantsSectionShown = hasGrants || (!hasReverseVesting && !isReverseVestingPending);

  if (!stakeholder) return;

  return (
    <>
      <tr className="flex w-full bg-white transition-colors" key={stakeholder.id}>
        <td className="p-0">
          <div className="flex h-full w-full items-center justify-center">
            <Button
              className={twMerge(
                'h-full cursor-pointer rounded-none p-4 transition-colors',
                isSelected && 'bg-brand-25',
              )}
              onClick={() => {
                setSelectedId(isSelected ? '' : stakeholder.id);
              }}
              styleType="NONE"
            >
              <ChevronDownIcon
                className={twMerge(
                  'ease-[cubic-bezier(0.4,0,0.2,1)] -rotate-90 transition-transform duration-300',
                  isSelected && 'rotate-0',
                )}
                iconColor={isSelected ? '#2565C8' : '#344054'}
              />
            </Button>
          </div>
        </td>
        <td className="w-[195px] text-nowrap p-4">
          <div className="flex w-full items-center gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-gray-200 text-sm font-[450] text-gray-700">
              {stakeholder.fullName[0]}
            </div>
            <span className="truncate text-sm font-[450] text-gray-700">
              {toUpperCaseWords(stakeholder.fullName)}
            </span>
          </div>
        </td>
        <td className="flex w-[129px] items-center text-nowrap p-4">
          <span className="truncate text-sm font-[450] text-gray-700">
            {toUpperCaseWords(stakeholder.type)}
          </span>
        </td>
        <td className="flex w-[129px] items-center text-nowrap p-4">
          <span className="truncate text-sm font-[450] text-gray-700">
            {toUpperCaseWords(stakeholderExTypeTitle[stakeholder.type])}
          </span>
        </td>
        <td className="flex w-[184px] items-center text-nowrap p-4">
          <TerminationTypeCombobox
            control={control}
            handleTerminationTypeChange={handleTerminationTypeChange}
            index={stakeholderIndex}
          />
        </td>
        <td className="flex w-[184px] items-center text-nowrap p-4">
          <DatePicker
            onSelect={(date) => {
              // Changing this date no longer releases either manual override. Last Vesting Date
              // is the primary as-of date — every settlement path reads it first and only falls
              // back to this one — and Final Day To Exercise is the operator's call in the same
              // way. Releasing them here is what used to make both fields read as whichever date
              // was touched last.
              setFormData(
                `terminateForm.stakeholderItems.${stakeholderIndex}.terminationDate`,
                date,
              );
            }}
            value={terminateForm?.stakeholderItems?.[stakeholderIndex]?.terminationDate}
            wrapperClassName="w-[152px]"
          >
            <FormInput
              autoComplete="off"
              control={control}
              disabledInput
              icon={<CalendarIcon className="size-5" iconColor="#344054" />}
              inputClassName="h-10"
              name={`terminateForm.stakeholderItems.${stakeholderIndex}.terminationDate`}
              transform={(value) => {
                return format(value, 'dd/MM/yyyy');
              }}
              wrapperClassName="w-full"
            />
          </DatePicker>
        </td>
        <td className="flex w-[184px] items-center text-nowrap p-4">
          {terminateForm?.stakeholderItems?.[stakeholderIndex]?.lastVestingDate ? (
            <DatePicker
              onSelect={(date) => {
                setFormData(
                  `terminateForm.stakeholderItems.${stakeholderIndex}.lastVestingDate`,
                  date,
                );
                setIsManually(true);
              }}
              value={terminateForm?.stakeholderItems?.[stakeholderIndex]?.lastVestingDate}
              wrapperClassName="w-[152px]"
            >
              <FormInput
                autoComplete="off"
                control={control}
                disabledInput
                icon={<CalendarIcon className="size-5" iconColor="#344054" />}
                inputClassName="h-10"
                name={`terminateForm.stakeholderItems.${stakeholderIndex}.lastVestingDate`}
                transform={(value) => {
                  return format(value, 'dd/MM/yyyy');
                }}
                wrapperClassName="w-full"
              />
            </DatePicker>
          ) : (
            <span className="text-sm font-[450] text-gray-700">n.a</span>
          )}
        </td>
        <td className="flex w-[190px] items-center text-nowrap p-4">
          <span className="truncate text-sm font-[450] text-gray-700">{stakeholder.email}</span>
        </td>
      </tr>

      {/* Height-animated so the row grows and shrinks instead of snapping. AnimatePresence
          holds the panel mounted until the collapse finishes, then unmounts it — the grant and
          reverse-vesting rows seed form state while mounted, so a collapsed stakeholder must
          not keep one alive. */}
      <AnimatePresence initial={false}>
        {isSelected && (
          <motion.div
            animate={{ height: 'auto', opacity: 1 }}
            className="w-full overflow-hidden"
            exit={{ height: 0, opacity: 0 }}
            initial={{ height: 0, opacity: 0 }}
            transition={{
              duration: 0.3,
              ease: [0.4, 0, 0.2, 1],
              opacity: { duration: 0.2 },
            }}
          >
            <div className="flex h-fit w-full flex-col gap-4 pt-4">
              {isGrantsSectionShown && (
                <SectionPanel
                  emptyMessage={<GrantsEmptyState stakeholderName={stakeholder.fullName} />}
                  isEmpty={!vestingTasks?.length}
                  titleId="GRANTS"
                >
                  <div className="h-fit w-full rounded-lg border border-gray-100 bg-white p-1">
                    <table className="w-full">
                      <thead>
                        <tr className="flex w-full bg-gray-50">
                          <td className="w-[156px] grow items-center px-4 py-3">
                            <span className="whitespace-nowrap text-label-md font-medium text-[#172335]">
                              <AppFormattedMessage id="GRANT" />
                            </span>
                          </td>
                          <td className="w-[122px] grow items-center px-4 py-3">
                            <span className="whitespace-nowrap text-label-md font-medium text-[#172335]">
                              <AppFormattedMessage id="ISSUE_DATE" />
                            </span>
                          </td>
                          <td className="w-[85px] grow items-center px-4 py-3">
                            <span className="whitespace-nowrap text-label-md font-medium text-[#172335]">
                              <AppFormattedMessage id="GRANTED" />
                            </span>
                          </td>
                          <td className="w-[85px] grow items-center px-4 py-3">
                            <span className="whitespace-nowrap text-label-md font-medium text-[#172335]">
                              <AppFormattedMessage id="VESTED" />
                            </span>
                          </td>
                          <td className="w-[85px] grow items-center px-4 py-3">
                            <span className="whitespace-nowrap text-label-md font-medium text-[#172335]">
                              Unvested
                            </span>
                          </td>
                          <td className="w-[85px] grow items-center px-4 py-3">
                            <span className="whitespace-nowrap text-label-md font-medium text-[#172335]">
                              <AppFormattedMessage id="EXERCISED" />
                            </span>
                          </td>
                          <td className="w-[85px] grow items-center px-4 py-3">
                            <span className="whitespace-nowrap text-label-md font-medium text-[#172335]">
                              <AppFormattedMessage id="EXERCISABLE" />
                            </span>
                          </td>
                          <td className="flex w-[184px] grow items-center gap-2 px-4 py-3">
                            <span className="whitespace-nowrap text-label-md font-medium text-[#172335]">
                              <AppFormattedMessage id="FINAL_DAY_TO_EXERCISE" />
                            </span>
                            <HoverCard>
                              <HoverCardTrigger>
                                <HelpIcon variant={HelpIconVariant.EXERCISE} />
                              </HoverCardTrigger>
                              <HoverCardContent
                                className="w-max rounded-lg bg-[#101828] px-3 py-2"
                                isShownTriangle
                                side="bottom"
                                triangleClassName="top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ml-0"
                              >
                                <span className="text-label-md font-[550] text-white">
                                  The deadline to exercise vested shares after termination.
                                </span>
                              </HoverCardContent>
                            </HoverCard>
                          </td>
                          <td className="w-[121px] grow items-center px-4 py-3">
                            <span className="whitespace-nowrap text-label-md font-medium text-[#172335]">
                              <AppFormattedMessage id="DAYS_REMAINING" />
                            </span>
                          </td>
                        </tr>
                      </thead>
                      <tbody className="divide-y-[1px] divide-gray-100">
                        {vestingTasks.map((task, index) => (
                          <GrantItem
                            clearErrors={clearErrors}
                            companyId={companyId}
                            control={control}
                            index={index}
                            isManually={isFinalDateManually}
                            isPredictionPending={isPredictionPending}
                            key={task.id}
                            prediction={predictionByTaskId[task.id]}
                            setError={setError}
                            setFormData={setFormData}
                            setIsManually={setIsFInalDateManually}
                            stakeholderId={stakeholder.id}
                            stakeholderIndex={stakeholderIndex}
                            task={task}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </SectionPanel>
              )}

              {hasReverseVesting && (
                <SectionPanel titleId="REVERSE_VESTING">
                  <ReverseVestingTable
                    clearErrors={clearErrors}
                    control={control}
                    isPending={isReverseVestingPending}
                    rows={reverseVestingRows}
                    setError={setError}
                    setFormData={setFormData}
                    stakeholderIndex={stakeholderIndex}
                  />
                </SectionPanel>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

type SectionPanelProps = {
  children: React.ReactNode;
  emptyMessage?: React.ReactNode;
  isEmpty?: boolean;
  titleId: StringKey;
};

/**
 * The GRANTS / REVERSE VESTING captions the design puts above each sub-table. Both sections are
 * independent — a stakeholder can hold grants, reverse-vested shares, or both.
 */
const SectionPanel: FC<SectionPanelProps> = ({ children, emptyMessage, isEmpty, titleId }) => (
  <div className="flex w-full flex-col gap-3 rounded-lg bg-gray-50 px-[2px] pb-[2px] pt-3">
    <span className="px-4 text-[10px] font-bold uppercase leading-4 tracking-[1px] text-gray-700">
      <AppFormattedMessage id={titleId} />
    </span>
    <div className="flex w-full items-center justify-center overflow-x-auto">
      {isEmpty ? emptyMessage : children}
    </div>
  </div>
);

type GrantsEmptyStateProps = {
  stakeholderName: string;
};

/**
 * Sits in the slot the Grants table would occupy, so the section keeps the card the operator
 * expects instead of collapsing to a bare sentence. The copy names the stakeholder because the
 * modal terminates several at once and each one gets its own section.
 */
const GrantsEmptyState: FC<GrantsEmptyStateProps> = ({ stakeholderName }) => (
  <div className="flex w-full flex-col items-center gap-3 rounded-lg border border-dashed border-gray-200 bg-white px-6 py-8 text-center">
    <div className="centered flex size-10 rounded-full bg-gray-50 ring-4 ring-gray-100">
      <EventGrant className="size-5" iconColor="#98A2B3" />
    </div>
    <div className="flex flex-col items-center gap-1">
      <span className="text-sm font-[550] text-gray-700">
        <AppFormattedMessage id="NO_UNFINISHED_GRANTS" />
      </span>
      <span className="max-w-[420px] text-xs font-[450] text-gray-500">
        <AppFormattedMessage
          id="NO_UNFINISHED_GRANTS_DESCRIPTION"
          values={{ name: stakeholderName }}
        />
      </span>
    </div>
  </div>
);

type GrantItemProps = {
  clearErrors: UseFormClearErrors<FormSchema>;
  companyId: string;
  control: Control<FormSchema>;
  index: number;
  isManually: boolean;
  isPredictionPending?: boolean;
  prediction?: { predictedReturnedToPool: number; predictedVestedShares: null | number };
  setError: UseFormSetError<FormSchema>;
  setFormData: UseFormSetValue<FormSchema>;
  setIsManually: (isManually: boolean) => void;
  stakeholderId: string;
  stakeholderIndex: number;
  task: VestingTask;
};

const GrantItem: FC<GrantItemProps> = ({
  control,
  setFormData,
  companyId,
  stakeholderIndex,
  stakeholderId,
  task,
  index,
  isManually,
  isPredictionPending,
  prediction,
  setIsManually,
  clearErrors,
  setError,
}) => {
  const { format } = useFormat();
  const { terminateForm } = useWatch<FormSchema>({ control });
  const { calcTerminationFinalExerciseDate } = StakeholderHook.useTerminationFinalExerciseDate();
  const isExercisable = task.exercisable;

  const vestedDisplay =
    prediction?.predictedVestedShares != null
      ? prediction.predictedVestedShares
      : task.vestedSharesCount;
  const unvestedDisplay = Math.max(0, (task.grantItem.numbersOfGrants ?? 0) - vestedDisplay);

  const terminationDateMs = coerceDateMs(
    terminateForm?.stakeholderItems?.[stakeholderIndex]?.terminationDate,
  );
  const lastVestingDateMs = coerceDateMs(
    terminateForm?.stakeholderItems?.[stakeholderIndex]?.lastVestingDate,
  );
  const terminationCause = terminateForm?.stakeholderItems?.[stakeholderIndex]?.terminationCause;
  const finalExerciseDate =
    terminateForm?.stakeholderItems?.[stakeholderIndex]?.grantItems?.[index]?.finalExerciseDate;
  const finalExerciseDateMs = coerceDateMs(finalExerciseDate);

  const beginFinalExerciseDateRequest = useRequestSequence();

  // Same live-state read as the stakeholder-level Last Vesting Date above: this row's projection
  // must not write over a Final Day To Exercise the operator picked while it was in flight.
  const finalExerciseStateRef = useRef({ finalExerciseDate, isManually });
  useEffect(() => {
    finalExerciseStateRef.current = { finalExerciseDate, isManually };
  }, [finalExerciseDate, isManually]);

  useEffect(() => {
    const terminationDate = terminateForm?.stakeholderItems?.[stakeholderIndex]?.terminationDate;
    const lastVestingDate = terminateForm?.stakeholderItems?.[stakeholderIndex]?.lastVestingDate;

    const isCurrentRequest = beginFinalExerciseDateRequest();

    if (terminationDate && terminationCause && !isManually) {
      const terminateData = {
        id: stakeholderId,
        terminationDate: toBackendDateFormat(terminationDate),
        terminationCause,
        ...(lastVestingDate ? { lastVestingDate: toBackendDateFormat(lastVestingDate) } : {}),
      };

      calcTerminationFinalExerciseDate(
        {
          companyId,
          vestingTaskId: task.id,
          data: terminateData,
        },
        {
          onSuccess: (data) => {
            const { finalExerciseDate: currentFinalExerciseDate, isManually: isManualNow } =
              finalExerciseStateRef.current;
            if (!isCurrentRequest() || isManualNow) return;

            const currentFinalExerciseDateMs = coerceDateMs(currentFinalExerciseDate);
            const nextFinalExerciseDateMs = coerceDateMs(data);

            if (data && currentFinalExerciseDateMs !== nextFinalExerciseDateMs) {
              setFormData(
                `terminateForm.stakeholderItems.${stakeholderIndex}.grantItems.${index}.finalExerciseDate`,
                data,
              );
            } else if (!data && currentFinalExerciseDate !== undefined) {
              setFormData(
                `terminateForm.stakeholderItems.${stakeholderIndex}.grantItems.${index}.finalExerciseDate`,
                undefined,
              );
            }
          },
        },
      );
    }
    // eslint-disable-next-line -- intentional hook deps (primitive ms fields, not stakeholderItems ref)
  }, [
    beginFinalExerciseDateRequest,
    calcTerminationFinalExerciseDate,
    companyId,
    index,
    isManually,
    lastVestingDateMs,
    setFormData,
    stakeholderId,
    stakeholderIndex,
    task.id,
    terminationCause,
    terminationDateMs,
  ]);

  useEffect(() => {
    const finalDate =
      terminateForm?.stakeholderItems?.[stakeholderIndex]?.grantItems?.[index]?.finalExerciseDate;
    const lastVestingDate = terminateForm?.stakeholderItems?.[stakeholderIndex]?.lastVestingDate;
    const terminationDate = terminateForm?.stakeholderItems?.[stakeholderIndex]?.terminationDate;
    const minExerciseFromDate = lastVestingDate ?? terminationDate;

    const path =
      `terminateForm.stakeholderItems.${stakeholderIndex}.grantItems.${index}.finalExerciseDate` as const;
    const finalMs = coerceDateMs(finalDate);
    const minMs = coerceDateMs(minExerciseFromDate);
    if (finalMs != null && minMs != null) {
      const fd = new Date(finalMs);
      fd.setHours(0, 0, 0, 0);
      const ld = new Date(minMs);
      ld.setHours(0, 0, 0, 0);
      if (fd.getTime() < ld.getTime()) {
        setError(path, {
          type: 'manual',
          message: lastVestingDate
            ? 'Date should be later than Last vesting date'
            : 'Date should be later than Termination date',
        });
        return;
      }
    }
    clearErrors(path);
    // eslint-disable-next-line -- intentional hook deps (primitive ms fields, not stakeholderItems ref)
  }, [
    clearErrors,
    finalExerciseDateMs,
    index,
    lastVestingDateMs,
    setError,
    stakeholderIndex,
    terminationDateMs,
  ]);

  return (
    <tr className="flex w-full bg-white">
      <td className="flex w-[156px] grow items-center px-4 py-3">
        <span className="truncate whitespace-nowrap text-sm font-[450] uppercase text-brand-700 underline">
          {task?.grantItem?.id?.split('-')[0]}
        </span>
      </td>
      <td className="flex w-[122px] grow items-center px-4 py-3">
        <span className="whitespace-nowrap text-sm font-[450] text-gray-700">
          {task.startTime ? format(task.grantItem.date, 'dd/MM/yyyy') : '-'}
        </span>
      </td>
      <td className="flex w-[85px] grow items-center px-4 py-3">
        <span className="whitespace-nowrap text-sm font-[450] text-gray-700">
          {task.grantItem.numbersOfGrants.toLocaleString('en-US')}
        </span>
      </td>
      <td className="flex w-[85px] grow items-center px-4 py-3">
        <span className="whitespace-nowrap text-sm font-[450] text-gray-700">
          {isPredictionPending && prediction === undefined
            ? '-'
            : vestedDisplay.toLocaleString('en-US')}
        </span>
      </td>
      <td className="flex w-[85px] grow items-center px-4 py-3">
        <span className="whitespace-nowrap text-sm font-[450] text-gray-700">
          {isPredictionPending && prediction === undefined
            ? '-'
            : unvestedDisplay.toLocaleString('en-US')}
        </span>
      </td>
      <td className="flex w-[85px] grow items-center px-4 py-3">
        <span className="whitespace-nowrap text-sm font-[450] text-gray-700">
          {isExercisable ? task.exercisedCount.toLocaleString('en-US') : '-'}
        </span>
      </td>
      <td className="flex w-[85px] grow items-center px-4 py-3">
        <span className="whitespace-nowrap text-sm font-[450] text-gray-700">
          {isExercisable ? (vestedDisplay - task.exercisedCount).toLocaleString('en-US') : '-'}
        </span>
      </td>
      <td className="flex w-[184px] grow items-center px-4 py-3">
        {terminateForm?.stakeholderItems?.[stakeholderIndex]?.grantItems?.[index]
          ?.finalExerciseDate ? (
          <DatePicker
            calendar={{
              fromDate:
                terminateForm?.stakeholderItems?.[stakeholderIndex]?.lastVestingDate ??
                terminateForm?.stakeholderItems?.[stakeholderIndex]?.terminationDate,
            }}
            onSelect={(date) => {
              setFormData(
                `terminateForm.stakeholderItems.${stakeholderIndex}.grantItems.${index}.finalExerciseDate`,
                date,
              );
              setIsManually(true);
            }}
            value={
              terminateForm?.stakeholderItems?.[stakeholderIndex]?.grantItems?.[index]
                ?.finalExerciseDate
            }
            wrapperClassName="w-[152px]"
          >
            <FormInput
              autoComplete="off"
              control={control}
              disabledInput
              icon={<CalendarIcon className="size-5" iconColor="#344054" />}
              inputClassName="h-10"
              name={`terminateForm.stakeholderItems.${stakeholderIndex}.grantItems.${index}.finalExerciseDate`}
              transform={(value) => {
                return format(value, 'dd/MM/yyyy');
              }}
              wrapperClassName="w-full"
            />
          </DatePicker>
        ) : (
          <span className="text-sm font-[450] text-gray-700">n.a</span>
        )}
      </td>
      <td className="flex w-[121px] grow items-center px-4 py-3">
        <span className="whitespace-nowrap text-sm font-[450] text-gray-700">
          {terminateForm?.stakeholderItems?.[stakeholderIndex]?.grantItems?.[index]
            ?.finalExerciseDate
            ? calcDayDifference(
                terminateForm?.stakeholderItems?.[stakeholderIndex]?.grantItems?.[index]
                  ?.finalExerciseDate,
                new Date(),
              )
            : '-'}
        </span>
      </td>
    </tr>
  );
};
